// components/access/UpgradeRequiredPage.jsx

import React, { useContext } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'
import { useNavigate } from 'react-router-dom'
import { UserContext } from '../../App'
import AmbuNetLogo from '../../assets/images/ambunetLogo.png'
import loaderGif from '../../assets/images/loader.gif'
import { openCrispChat } from '../../helpers/useCrisp'
import planAllowances from '../../helpers/planAllowances.json'
import {
  concurrentRoomLimitMessage,
  controlRoomHoursLimitMessage,
} from '../../helpers/controlRoomCapacity'

// The allowances that can be "reached": adding an employee, a vehicle or a
// base location beyond the package's capacity, or starting / reopening a
// control room while the package's concurrent control rooms are all open, is
// refused by the server, as is (Premium only) starting or reopening one once
// the month's control-room hours are used. PTS journeys, and control-room
// hours on Enterprise, are recorded for review only and never arrive here. `body` replaces the generic "used X of
// the Y" wording where that would read badly.
const LIMIT_COPY = {
  employees: { title: 'Employee', plural: 'employees' },
  vehicles: { title: 'Vehicle', plural: 'vehicles' },
  baseLocations: { title: 'Base location', plural: 'base locations' },
  concurrentControlRooms: {
    title: 'Concurrent control room',
    plural: 'concurrent control rooms',
    body: (limitInfo) => concurrentRoomLimitMessage(limitInfo),
  },
  controlRoomHoursPerMonth: {
    title: 'Control-room hours',
    plural: 'control-room hours',
    body: (limitInfo) => controlRoomHoursLimitMessage(limitInfo),
  },
}

// Figures come from the plan catalogue mirror, never typed in here.
const premiumAllowances = planAllowances.plans.premium.allowances
const enterpriseAllowances = planAllowances.plans.enterprise.allowances

const baseLocationsText = (count) =>
  `${count} base location${count === 1 ? '' : 's'}`

const NEXT_TIER_BENEFITS = {
  Premium: [
    `Up to ${premiumAllowances.employees} employees & ${premiumAllowances.vehicles} vehicles`,
    baseLocationsText(premiumAllowances.baseLocations),
    'Recruitment portal & CQC Centre',
    'Audits, risk register & KPI dashboards',
    `Command + Control, with one control room at a time and ${premiumAllowances.controlRoomHoursPerMonth} control-room hours per calendar month`,
  ],
  Enterprise: [
    `Up to ${enterpriseAllowances.employees} employees & ${enterpriseAllowances.vehicles} vehicles`,
    baseLocationsText(enterpriseAllowances.baseLocations),
    `Command + Control, with up to ${enterpriseAllowances.concurrentControlRooms} control rooms open at the same time and ${enterpriseAllowances.controlRoomHoursPerMonth} control-room hours per calendar month`,
    `Patient Transport, with ${enterpriseAllowances.ptsJourneysPerMonth} PTS journeys per calendar month`,
  ],
  'Enterprise+': [
    'Employee, vehicle & base location allowances tailored to your operation',
    'More concurrent control rooms, plus larger PTS journey & control-room hour review allowances',
    'Everything in Enterprise',
    'Individually quoted by the AmbuNet team',
  ],
}

export default function UpgradeRequiredPage({
  requiredTier,
  featureName = 'Feature',
  benefits,
  areNotIs,
  limitInfo,
  setUpgradeRequired,
  onGoBack,
}) {
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const allowedTeams = ['Personnel', 'Fleet', 'Governance', 'Manager']

  // Usage-limit mode: the package's employee, vehicle or base-location
  // capacity has been reached. Tenants below Enterprise are pointed at the
  // next tier; Enterprise tenants are pointed at an Enterprise+ quote via
  // support, since there is no self-serve upgrade above Enterprise.
  const limitCopy = limitInfo
    ? (LIMIT_COPY[limitInfo.limitType] ?? {
        title: 'Usage',
        plural: 'included allowances',
      })
    : null
  const atTopTier = Number(limitInfo?.currentTier ?? 0) >= 3
  const requiredTierName = limitInfo
    ? atTopTier
      ? 'Enterprise+'
      : Number(limitInfo.currentTier) >= 2
        ? 'Enterprise'
        : 'Premium'
    : requiredTier <= 2
      ? 'Premium'
      : 'Enterprise'

  const isBillPayer =
    (user?.security?.isSiteAdmin ||
      user?.employee?.teams?.some((x) => allowedTeams.includes(x))) ??
    false
  //   const isBillPayer = false

  const defaultBenefits = limitInfo
    ? (NEXT_TIER_BENEFITS[requiredTierName] ?? [])
    : [
        'Access additional operational workflows',
        'Unlock more tools for your organisation',
        'Support safer, more joined-up working',
        'Reduce duplicated admin across teams',
      ]

  const limitHeading = limitCopy ? `${limitCopy.title} limit reached` : null
  const limitUsage =
    limitInfo?.used != null && limitInfo?.limit != null
      ? `${limitInfo.used} of the ${limitInfo.limit}`
      : `all of the`
  const limitBodyText = limitCopy
    ? limitCopy.body
      ? limitCopy.body(limitInfo)
      : atTopTier
      ? `Your organisation has used ${limitUsage} ${limitCopy.plural} included with the Enterprise package. Enterprise+ packages are tailored to larger operations — contact AmbuNet support for a quote.`
      : `Your organisation has used ${limitUsage} ${limitCopy.plural} included in your current package. Upgrade to ${requiredTierName} to increase your allowance.`
    : null
  const limitPanelText = atTopTier
    ? `Enterprise+ raises your allowances with a package quoted individually for your operation — message our support team and we'll put a quote together.`
    : `The ${requiredTierName} package includes larger allowances alongside more advanced AmbuNet workflows, and upgrading is self-serve.`

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: '100%', lg: '75%' },
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.18)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
        //   background: 'rgba(255,255,255,0.96)',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          p: 3,
          // background:
          // 'linear-gradient(135deg, #242F40 0%, #2c3e50 65%, #239AA4 150%)',
          background:
            'radial-gradient(circle at top left, rgba(35,154,164,0.22), transparent 35%), linear-gradient(135deg, #1A2230 0%, #242F40 45%, #101827 100%)',

          color: 'white',
          textAlign: 'center',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} lg={!isBillPayer ? 12 : 6}>
            <Stack gap={2}>
              {isBillPayer && (
                <Chip
                  icon={<LockRoundedIcon sx={{ color: 'white !important' }} />}
                  label={
                    limitInfo
                      ? atTopTier
                        ? 'Enterprise+'
                        : `Upgrade to ${requiredTierName}`
                      : `${requiredTierName} feature`
                  }
                  sx={{
                    alignSelf: 'flex-start',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.22)',
                    fontWeight: 800,
                  }}
                />
              )}

              <AdminPanelSettingsRoundedIcon
                sx={{ fontSize: 64, alignSelf: 'center', opacity: 0.9 }}
              />
              <Typography variant="h5" fontWeight={900} color="white">
                {limitInfo
                  ? limitHeading
                  : isBillPayer
                    ? `Unlock ${featureName}`
                    : `${featureName} unavailable`}
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.65, mt: 1, color: '#fff' }}
              >
                {limitInfo
                  ? isBillPayer
                    ? limitBodyText
                    : `Your organisation has reached its ${limitCopy?.plural ?? 'usage'} allowance. Please speak to your organisation's AmbuNet administrator.`
                  : isBillPayer
                    ? `${featureName} ${areNotIs ? 'are' : 'is'} available on the ${requiredTierName} package. Upgrade your organisation's AmbuNet plan to enable this functionality for your team.`
                    : `If you believe you need access to this area, please speak to your organisation's AmbuNet administrator.`}
              </Typography>
              {isBillPayer ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 4,
                    border: '1px solid rgba(255,255,255,0.16)',
                    background: 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(14px)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.68)', mt: 0.4 }}
                    >
                      {limitInfo
                        ? limitPanelText
                        : `The ${requiredTierName} package gives your organisation access to more advanced AmbuNet workflows and operational tools.`}
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => {
                    if (setUpgradeRequired) {
                      setUpgradeRequired(false)
                    } else if (onGoBack) {
                      onGoBack()
                    } else {
                      navigate(-1)
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    borderRadius: 2,
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'contrast.main',
                    },
                  }}
                >
                  {setUpgradeRequired ? 'Close' : 'Go back'}
                </Button>
              )}
            </Stack>
          </Grid>
          {isBillPayer && (
            <Grid item xs={12} lg={6}>
              <Box height="100%">
                <Stack gap={2} justifyContent="space-between" height="100%">
                  <Box display={{ xs: 'none', lg: 'block' }} p={3}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={900}
                      mb={2}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        fontSize: '0.72rem',
                        color: 'rgba(255, 255, 255, 0.85)',
                        textAlign: 'center',
                      }}
                    >
                      {limitInfo
                        ? `What ${requiredTierName} offers`
                        : 'What this unlocks'}
                    </Typography>

                    <Stack spacing={1.3}>
                      {(benefits?.length ? benefits : defaultBenefits).map(
                        (benefit, index) => (
                          <Box
                            key={index}
                            sx={{
                              px: 2,
                              py: 1.4,
                              borderRadius: 3,
                              background: 'rgba(255,255,255,0.06)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(35,154,164,0.18)',
                                  color: '#46D7E1',
                                  flexShrink: 0,
                                }}
                              >
                                <CheckCircleRoundedIcon sx={{ fontSize: 12 }} />
                              </Box>

                              <Typography
                                sx={{
                                  color: 'rgba(255,255,255,0.88)',
                                  fontWeight: 500,
                                  fontSize: '0.8rem',
                                  lineHeight: 1.3,
                                  textAlign: 'start',
                                }}
                              >
                                {benefit}
                              </Typography>
                            </Stack>
                          </Box>
                        ),
                      )}
                    </Stack>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    px={3}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackRoundedIcon />}
                      onClick={() => {
                        if (setUpgradeRequired) {
                          setUpgradeRequired(false)
                        } else if (onGoBack) {
                          onGoBack()
                        } else {
                          navigate(-1)
                        }
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        borderRadius: 2,
                        borderColor: 'white',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'contrast.main',
                        },
                      }}
                    >
                      {setUpgradeRequired ? 'Close' : 'Go back'}
                    </Button>

                    {limitInfo && atTopTier ? (
                      <Button
                        variant="contained"
                        startIcon={<SupportAgentRoundedIcon />}
                        onClick={() => {
                          setUpgradeRequired && setUpgradeRequired(false)
                          openCrispChat()
                        }}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: '#239AA4',
                          '&:hover': {
                            backgroundColor: '#1D858E',
                          },
                        }}
                      >
                        Contact AmbuNet Support
                      </Button>
                    ) : (
                      user?.security?.isSiteAdmin && (
                        <Button
                          variant="contained"
                          startIcon={<WorkspacePremiumRoundedIcon />}
                          onClick={() =>
                            navigate('/site-configuration/billing')
                          }
                          sx={{
                            textTransform: 'none',
                            fontWeight: 900,
                            borderRadius: 2,
                            backgroundColor: '#239AA4',
                            '&:hover': {
                              backgroundColor: '#1D858E',
                            },
                          }}
                        >
                          View upgrade options
                        </Button>
                      )
                    )}
                  </Stack>
                </Stack>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Paper>
  )
}
